import { format } from "date-fns/format";
import { isAfter } from "date-fns/isAfter";
import { isToday } from "date-fns/isToday";
import { eachDayOfInterval } from "date-fns/eachDayOfInterval";
import { shouldWaiveBookingTax } from "../../utils/facilityEvents";

export type DateSelectionType = "single" | "multiple" | "range";

export type DateAvailability = {
  date: string;
  bookedSlots: any[];
  loading: boolean;
  error: string | null;
  hasOverlap: boolean;
};

export type BookingDraft = {
  id: string;
  expanded: boolean;
  sameDay: boolean;
  sameTime: boolean;
  selectedLaneId: string;
  dateSelectionType: DateSelectionType;
  selectedDate?: Date;
  selectedDates: Date[];
  dateRange: {
    from: Date | undefined;
    to: Date | undefined;
  };
  startTime: string;
  endTime: string;
  useIndividualTimes: boolean;
  individualDateTimes: Record<string, { startTime: string; endTime: string }>;
  dateAvailabilities: DateAvailability[];
  hasOverlap: boolean;
};

export type LaneOption = {
  id: string;
  name: string;
  pricePerHour: number;
  type?: string;
};

export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatTimeForDisplay = (timeStr: string): string => {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":").map(Number);
  const hour = hours % 12 || 12;
  const ampm = hours >= 12 ? "PM" : "AM";
  return `${hour}:${minutes.toString().padStart(2, "0")} ${ampm}`;
};

export const roundMoney = (amount: number): number => {
  if (!Number.isFinite(amount)) return 0;
  return Math.round((amount + Number.EPSILON) * 100) / 100;
};

export const formatCurrency = (amount: number): string => {
  return roundMoney(amount).toLocaleString("en-US", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatDuration = (duration: number): string => {
  if (duration === 0) return "No duration set";

  if (duration < 1) {
    return `${duration * 60} minutes`;
  }

  const fullHours = Math.floor(duration);
  const hasHalfHour = duration % 1 !== 0;

  if (hasHalfHour) {
    return `${fullHours} hour${fullHours !== 1 ? "s" : ""} 30 minutes`;
  }

  return `${fullHours} hour${fullHours !== 1 ? "s" : ""}`;
};

export const createEmptyDraft = (expanded = true): BookingDraft => ({
  id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  expanded,
  sameDay: false,
  sameTime: false,
  selectedLaneId: "",
  dateSelectionType: "single",
  selectedDate: undefined,
  selectedDates: [],
  dateRange: { from: undefined, to: undefined },
  startTime: "",
  endTime: "",
  useIndividualTimes: false,
  individualDateTimes: {},
  dateAvailabilities: [],
  hasOverlap: false,
});

export const copyDayFromSource = (
  draft: BookingDraft,
  source: BookingDraft
): BookingDraft => ({
  ...draft,
  dateSelectionType: source.dateSelectionType,
  selectedDate: source.selectedDate ? new Date(source.selectedDate) : undefined,
  selectedDates: source.selectedDates.map((date) => new Date(date)),
  dateRange: {
    from: source.dateRange.from ? new Date(source.dateRange.from) : undefined,
    to: source.dateRange.to ? new Date(source.dateRange.to) : undefined,
  },
});

export const copyTimeFromSource = (
  draft: BookingDraft,
  source: BookingDraft
): BookingDraft => {
  const next: BookingDraft = {
    ...draft,
    startTime: source.startTime,
    endTime: source.endTime,
  };

  if (draft.sameDay && source.useIndividualTimes) {
    next.useIndividualTimes = source.useIndividualTimes;
    next.individualDateTimes = { ...source.individualDateTimes };
  }

  return next;
};

export const getDatesToCheck = (draft: BookingDraft): string[] => {
  switch (draft.dateSelectionType) {
    case "single":
      return draft.selectedDate ? [formatDateToYYYYMMDD(draft.selectedDate)] : [];
    case "multiple":
      return draft.selectedDates.map((date) => formatDateToYYYYMMDD(date));
    case "range":
      if (draft.dateRange.from && draft.dateRange.to) {
        return eachDayOfInterval({
          start: draft.dateRange.from,
          end: draft.dateRange.to,
        }).map((date) => formatDateToYYYYMMDD(date));
      }
      return [];
    default:
      return draft.selectedDate ? [formatDateToYYYYMMDD(draft.selectedDate)] : [];
  }
};

const hoursBetween = (startTime: string, endTime: string): number => {
  if (!startTime || !endTime) return 0;
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);
  const startDate = new Date();
  startDate.setHours(startHours, startMinutes, 0, 0);
  const endDate = new Date();
  endDate.setHours(endHours, endMinutes, 0, 0);
  if (endDate < startDate) {
    endDate.setDate(endDate.getDate() + 1);
  }
  const durationMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);
  return Math.max(0.5, Number.parseFloat((durationMinutes / 60).toFixed(1)));
};

export const getCurrentDuration = (draft: BookingDraft): number => {
  if (draft.dateSelectionType === "multiple" && draft.useIndividualTimes) {
    let totalDuration = 0;
    for (const date of draft.selectedDates) {
      const dateKey = formatDateToYYYYMMDD(date);
      const dateTimes = draft.individualDateTimes[dateKey];
      if (dateTimes?.startTime && dateTimes?.endTime) {
        totalDuration += hoursBetween(dateTimes.startTime, dateTimes.endTime);
      }
    }
    return Math.max(0, Number.parseFloat(totalDuration.toFixed(1)));
  }

  if (!draft.startTime || !draft.endTime) return 0;
  return hoursBetween(draft.startTime, draft.endTime);
};

export const getNumberOfDays = (draft: BookingDraft): number => {
  if (draft.dateSelectionType === "multiple" && !draft.useIndividualTimes) {
    return draft.selectedDates.length || 1;
  }
  if (draft.dateSelectionType === "range" && draft.dateRange.from && draft.dateRange.to) {
    const startDate = new Date(draft.dateRange.from);
    const endDate = new Date(draft.dateRange.to);
    return (
      Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
    );
  }
  return 1;
};

export const getTotalDuration = (draft: BookingDraft): number => {
  const singleDayDuration = getCurrentDuration(draft);
  if (singleDayDuration === 0) return 0;
  if (draft.dateSelectionType === "multiple" && !draft.useIndividualTimes) {
    return singleDayDuration * draft.selectedDates.length;
  }
  if (draft.dateSelectionType === "range" && draft.dateRange.from && draft.dateRange.to) {
    return singleDayDuration * getNumberOfDays(draft);
  }
  return singleDayDuration;
};

export const getHourlyPrice = (draft: BookingDraft, lanes: LaneOption[]): number => {
  if (!draft.selectedLaneId) return 0;
  return lanes.find((lane) => lane.id === draft.selectedLaneId)?.pricePerHour || 0;
};

const priceForHours = (hourlyPrice: number, duration: number): number => {
  const fullHours = Math.floor(duration);
  const hasHalfHour = duration % 1 !== 0;
  let subtotal = fullHours * hourlyPrice;
  if (hasHalfHour) {
    subtotal += hourlyPrice * 0.5;
  }
  return roundMoney(subtotal);
};

export const calculateDraftSubtotal = (
  draft: BookingDraft,
  lanes: LaneOption[],
  discountPercent: number
): number => {
  const hourlyPrice = getHourlyPrice(draft, lanes);
  let subtotal = 0;

  if (draft.dateSelectionType === "multiple" && draft.useIndividualTimes) {
    for (const date of draft.selectedDates) {
      const dateKey = formatDateToYYYYMMDD(date);
      const dateTimes = draft.individualDateTimes[dateKey];
      if (dateTimes?.startTime && dateTimes?.endTime) {
        subtotal += priceForHours(
          hourlyPrice,
          hoursBetween(dateTimes.startTime, dateTimes.endTime)
        );
      }
    }
  } else {
    const duration = getCurrentDuration(draft);
    if (duration === 0) return 0;
    subtotal = priceForHours(hourlyPrice, duration) * getNumberOfDays(draft);
  }

  const safeDiscount = Math.min(100, Math.max(0, discountPercent || 0));
  return roundMoney(subtotal * (1 - safeDiscount / 100));
};

export const calculateDraftTax = (
  draft: BookingDraft,
  lanes: LaneOption[],
  discountPercent: number,
  taxRate: number
): number => {
  const subtotal = calculateDraftSubtotal(draft, lanes, discountPercent);
  const dates = getDatesToCheck(draft);
  if (dates.length === 0) {
    return roundMoney(subtotal * (taxRate / 100));
  }
  const perDay = subtotal / dates.length;
  return roundMoney(
    dates.reduce((sum, date) => {
      const rate = shouldWaiveBookingTax(date) ? 0 : taxRate / 100;
      return sum + perDay * rate;
    }, 0)
  );
};

export const isDraftComplete = (draft: BookingDraft): boolean => {
  if (!draft.selectedLaneId) return false;

  switch (draft.dateSelectionType) {
    case "single":
      if (!draft.selectedDate) return false;
      break;
    case "multiple":
      if (draft.selectedDates.length === 0) return false;
      break;
    case "range":
      if (!draft.dateRange.from || !draft.dateRange.to) return false;
      break;
    default:
      return false;
  }

  if (draft.dateSelectionType === "multiple" && draft.useIndividualTimes) {
    return draft.selectedDates.every((date) => {
      const dateTimes = draft.individualDateTimes[formatDateToYYYYMMDD(date)];
      return Boolean(dateTimes?.startTime && dateTimes?.endTime);
    });
  }

  return Boolean(draft.startTime && draft.endTime);
};

export const getDraftValidationError = (draft: BookingDraft, index: number): string | null => {
  const label = `Booking ${index + 1}`;

  if (!draft.selectedLaneId) {
    return `${label}: please select a lane or cage`;
  }

  switch (draft.dateSelectionType) {
    case "single":
      if (!draft.selectedDate) return `${label}: please select a date`;
      break;
    case "multiple":
      if (draft.selectedDates.length === 0) {
        return `${label}: please select at least one date`;
      }
      break;
    case "range":
      if (!draft.dateRange.from || !draft.dateRange.to) {
        return `${label}: please select a date range`;
      }
      break;
    default:
      return `${label}: please select a date`;
  }

  if (draft.dateSelectionType === "multiple" && draft.useIndividualTimes) {
    for (const date of draft.selectedDates) {
      const dateTimes = draft.individualDateTimes[formatDateToYYYYMMDD(date)];
      if (!dateTimes?.startTime || !dateTimes?.endTime) {
        return `${label}: please select start and end times for ${format(date, "MMMM d")}`;
      }
    }
  } else if (!draft.startTime || !draft.endTime) {
    return `${label}: please select both start and end times`;
  }

  if (draft.hasOverlap) {
    return `${label}: this time slot is already booked`;
  }

  try {
    if (draft.dateSelectionType === "multiple" && draft.useIndividualTimes) {
      for (const date of draft.selectedDates) {
        const dateTimes = draft.individualDateTimes[formatDateToYYYYMMDD(date)];
        if (!dateTimes) continue;
        const [startHours, startMinutes] = dateTimes.startTime.split(":").map(Number);
        const [endHours, endMinutes] = dateTimes.endTime.split(":").map(Number);
        const startDateTime = new Date(date);
        startDateTime.setHours(startHours, startMinutes, 0, 0);
        const endDateTime = new Date(date);
        endDateTime.setHours(endHours, endMinutes, 0, 0);
        if (endDateTime < startDateTime) {
          endDateTime.setDate(endDateTime.getDate() + 1);
        }
        if (!isAfter(endDateTime, startDateTime)) {
          return `${label}: end time must be after start time for ${format(date, "MMMM d")}`;
        }
        if (isToday(date) && !isAfter(startDateTime, new Date())) {
          return `${label}: booking must be for a future time for ${format(date, "MMMM d")}`;
        }
      }
    } else {
      const referenceDate =
        draft.dateSelectionType === "single"
          ? draft.selectedDate
          : draft.dateSelectionType === "multiple"
            ? draft.selectedDates[0]
            : draft.dateRange.from;
      if (!referenceDate) return `${label}: please select a date`;
      const [startHours, startMinutes] = draft.startTime.split(":").map(Number);
      const [endHours, endMinutes] = draft.endTime.split(":").map(Number);
      const startDateTime = new Date(referenceDate);
      startDateTime.setHours(startHours, startMinutes, 0, 0);
      const endDateTime = new Date(referenceDate);
      endDateTime.setHours(endHours, endMinutes, 0, 0);
      if (endDateTime < startDateTime) {
        endDateTime.setDate(endDateTime.getDate() + 1);
      }
      if (!isAfter(endDateTime, startDateTime)) {
        return `${label}: end time must be after start time`;
      }
      if (isToday(referenceDate) && !isAfter(startDateTime, new Date())) {
        return `${label}: booking must be for a future time`;
      }
    }
  } catch {
    return `${label}: invalid time format`;
  }

  return null;
};

export const getDraftTimeSlots = (
  draft: BookingDraft
): { date: string; startTime: string; endTime: string }[] => {
  const dates = getDatesToCheck(draft);
  if (draft.dateSelectionType === "multiple" && draft.useIndividualTimes) {
    return dates
      .map((date) => {
        const times = draft.individualDateTimes[date];
        if (!times?.startTime || !times?.endTime) return null;
        return { date, startTime: times.startTime, endTime: times.endTime };
      })
      .filter(Boolean) as { date: string; startTime: string; endTime: string }[];
  }
  if (!draft.startTime || !draft.endTime) return [];
  return dates.map((date) => ({
    date,
    startTime: draft.startTime,
    endTime: draft.endTime,
  }));
};

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

export const timesOverlap = (
  startA: string,
  endA: string,
  startB: string,
  endB: string
): boolean => {
  const selectedStart = timeToMinutes(startA);
  const selectedEnd = timeToMinutes(endA);
  const slotStart = timeToMinutes(startB);
  const slotEnd = timeToMinutes(endB);
  return (
    selectedStart < slotEnd &&
    selectedEnd > slotStart &&
    !(selectedEnd === slotStart || selectedStart === slotEnd)
  );
};

export const facilitiesConflict = (
  a: LaneOption | undefined,
  b: LaneOption | undefined
): boolean => {
  if (!a || !b) return false;
  if (a.id === b.id) return true;
  const aType = (a.type || "").toLowerCase();
  const bType = (b.type || "").toLowerCase();
  if (aType === "full_facility" || bType === "full_facility") return true;
  if (aType === "training_cage" && bType === "lane") return true;
  if (bType === "training_cage" && aType === "lane") return true;
  return false;
};

export const draftsOverlapEachOther = (
  drafts: BookingDraft[],
  lanes: LaneOption[]
): { index: number; message: string } | null => {
  for (let i = 0; i < drafts.length; i++) {
    const aSlots = getDraftTimeSlots(drafts[i]);
    const aLane = lanes.find((lane) => lane.id === drafts[i].selectedLaneId);
    for (let j = i + 1; j < drafts.length; j++) {
      const bSlots = getDraftTimeSlots(drafts[j]);
      const bLane = lanes.find((lane) => lane.id === drafts[j].selectedLaneId);
      if (!facilitiesConflict(aLane, bLane)) continue;
      for (const aSlot of aSlots) {
        for (const bSlot of bSlots) {
          if (
            aSlot.date === bSlot.date &&
            timesOverlap(aSlot.startTime, aSlot.endTime, bSlot.startTime, bSlot.endTime)
          ) {
            return {
              index: j,
              message: `Booking ${j + 1} overlaps Booking ${i + 1} on ${aSlot.date}.`,
            };
          }
        }
      }
    }
  }
  return null;
};

export const siblingSlotsForDraft = (
  draft: BookingDraft,
  allDrafts: BookingDraft[],
  lanes: LaneOption[]
): any[] => {
  const lane = lanes.find((item) => item.id === draft.selectedLaneId);
  const slots: any[] = [];
  for (const other of allDrafts) {
    if (other.id === draft.id) continue;
    const otherLane = lanes.find((item) => item.id === other.selectedLaneId);
    if (!facilitiesConflict(lane, otherLane)) continue;
    for (const slot of getDraftTimeSlots(other)) {
      const [year, month, day] = slot.date.split("-").map(Number);
      const [startH, startM] = slot.startTime.split(":").map(Number);
      const [endH, endM] = slot.endTime.split(":").map(Number);
      const start = new Date(year, month - 1, day, startH, startM, 0, 0);
      const end = new Date(year, month - 1, day, endH, endM, 0, 0);
      if (end <= start) {
        end.setDate(end.getDate() + 1);
      }
      slots.push({
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        date: slot.date,
      });
    }
  }
  return slots;
};

export const getSameDayTimeChip = (draft: BookingDraft, isFirst: boolean): string | null => {
  if (isFirst) return null;
  if (draft.sameDay && draft.sameTime) return "Same day, same time";
  if (draft.sameDay) return "Same day";
  if (draft.sameTime) return "Same time";
  return "Different day";
};

export const getDraftCardDateLabel = (draft: BookingDraft): string => {
  if (draft.dateSelectionType === "single" && draft.selectedDate) {
    return format(draft.selectedDate, "EEEE MMM d");
  }
  if (draft.dateSelectionType === "multiple" && draft.selectedDates.length > 0) {
    if (draft.selectedDates.length === 1) {
      return format(draft.selectedDates[0], "EEEE MMM d");
    }
    return `${draft.selectedDates.length} dates`;
  }
  if (draft.dateRange.from && draft.dateRange.to) {
    return `${format(draft.dateRange.from, "MMM d")} – ${format(draft.dateRange.to, "MMM d")}`;
  }
  return "No date selected";
};

export const getDraftCardTimeLabel = (draft: BookingDraft): string => {
  if (draft.dateSelectionType === "multiple" && draft.useIndividualTimes) {
    return "Individual times";
  }
  if (!draft.startTime || !draft.endTime) return "No time selected";
  return `${formatTimeForDisplay(draft.startTime)} – ${formatTimeForDisplay(draft.endTime)}`;
};

export const getFormattedDraftTime = (draft: BookingDraft): string => {
  if (draft.dateSelectionType === "multiple" && draft.useIndividualTimes) {
    if (draft.selectedDates.length === 0) return "...";
    return draft.selectedDates
      .map((date) => {
        const dateTimes = draft.individualDateTimes[formatDateToYYYYMMDD(date)];
        if (!dateTimes?.startTime || !dateTimes?.endTime) {
          return `${format(date, "MMM d")}: No time selected`;
        }
        return `${format(date, "MMM d")}: ${formatTimeForDisplay(dateTimes.startTime)} - ${formatTimeForDisplay(dateTimes.endTime)}`;
      })
      .join(", ");
  }

  if (!draft.startTime || !draft.endTime) return "...";

  try {
    switch (draft.dateSelectionType) {
      case "single":
        if (!draft.selectedDate) return "...";
        return `${format(draft.selectedDate, "EEE MMM d")}, ${formatTimeForDisplay(draft.startTime)} - ${formatTimeForDisplay(draft.endTime)}`;
      case "multiple":
        if (draft.selectedDates.length === 0) return "...";
        return `${draft.selectedDates.length} dates, ${formatTimeForDisplay(draft.startTime)} - ${formatTimeForDisplay(draft.endTime)}`;
      case "range":
        if (!draft.dateRange.from || !draft.dateRange.to) return "...";
        return `${format(draft.dateRange.from, "MMM d")} - ${format(draft.dateRange.to, "MMM d")}, ${formatTimeForDisplay(draft.startTime)} - ${formatTimeForDisplay(draft.endTime)}`;
      default:
        return "...";
    }
  } catch {
    return "...";
  }
};

export const buildAdminBookingPayload = (
  draft: BookingDraft,
  lanes: LaneOption[],
  userDetails: {
    name: string;
    email: string;
    phone: string;
    address: string;
  },
  specialRequests: string,
  paymentMethod: string,
  taxRate: number,
  discountPercent: number
) => {
  let bookingDates: any;
  let bookingTimes: { startTime: string; endTime: string } | null = null;

  switch (draft.dateSelectionType) {
    case "single":
      bookingDates = draft.selectedDate
        ? formatDateToYYYYMMDD(draft.selectedDate)
        : "";
      bookingTimes = { startTime: draft.startTime, endTime: draft.endTime };
      break;
    case "multiple":
      if (draft.useIndividualTimes) {
        bookingDates = draft.selectedDates.map((date) => {
          const dateKey = formatDateToYYYYMMDD(date);
          const dateTimes = draft.individualDateTimes[dateKey];
          return {
            date: dateKey,
            startTime: dateTimes.startTime,
            endTime: dateTimes.endTime,
          };
        });
        bookingTimes = null;
      } else {
        bookingDates = draft.selectedDates.map((date) => formatDateToYYYYMMDD(date));
        bookingTimes = { startTime: draft.startTime, endTime: draft.endTime };
      }
      break;
    case "range":
      bookingDates = {
        startDate: draft.dateRange.from
          ? formatDateToYYYYMMDD(draft.dateRange.from)
          : "",
        endDate: draft.dateRange.to ? formatDateToYYYYMMDD(draft.dateRange.to) : "",
      };
      bookingTimes = { startTime: draft.startTime, endTime: draft.endTime };
      break;
    default:
      bookingDates = "";
  }

  const selectedIndividualDateTimes =
    draft.dateSelectionType === "multiple" && draft.useIndividualTimes
      ? Object.fromEntries(
          draft.selectedDates.map((date) => {
            const dateKey = formatDateToYYYYMMDD(date);
            return [dateKey, draft.individualDateTimes[dateKey]];
          })
        )
      : null;

  const selectedLane = lanes.find((lane) => lane.id === draft.selectedLaneId);
  const subtotal = calculateDraftSubtotal(draft, lanes, discountPercent);
  const taxAmount = calculateDraftTax(draft, lanes, discountPercent, taxRate);

  return {
    userDetails,
    laneOrCageId: draft.selectedLaneId,
    laneOrCage: selectedLane,
    startTime: bookingTimes?.startTime || null,
    endTime: bookingTimes?.endTime || null,
    bookingDates,
    individualDateTimes: selectedIndividualDateTimes,
    specialRequests,
    bookingStatus: paymentMethod,
    paymentStatus: paymentMethod,
    hourlyRate: roundMoney(getHourlyPrice(draft, lanes)),
    duration: getCurrentDuration(draft),
    subtotal: roundMoney(subtotal),
    taxRate,
    taxAmount: roundMoney(taxAmount),
    totalAmount: roundMoney(subtotal + taxAmount),
    discount: discountPercent,
  };
};
